import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Shared plumbing for every form on the site.
 *
 * The bot defence is invisible and lives half here, half on the server: this
 * side fetches a signed timing token when the form mounts and renders a
 * honeypot field positioned off-screen rather than display:none, which the
 * better scrapers check for. The server does the actual judging.
 */
export function useEnquiryForm(kind) {
  const [token, setToken] = useState('');
  const [state, setState] = useState('idle');   // idle | sending | done | error
  const [error, setError] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    let live = true;
    fetch('/api/form-token')
      .then((r) => r.json())
      .then((d) => { if (live) setToken(d.token); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  async function submit(event) {
    event.preventDefault();
    if (state === 'sending') return;

    const data = Object.fromEntries(new FormData(event.currentTarget).entries());
    setState('sending');
    setError(null);

    try {
      const res = await fetch('/api/enquiries', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...data, kind, token, source_path: pathname }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Something went wrong. Try again, or email us directly.');
      }
      setState('done');
    } catch (err) {
      setError(err.message);
      setState('error');
    }
  }

  return { submit, state, error, sending: state === 'sending', done: state === 'done' };
}

/** The honeypot. A person never sees it; a script fills it in. */
export function Honeypot() {
  return (
    <div className="hp" aria-hidden="true">
      <label>
        Website
        <input type="text" name="website" tabIndex={-1} autoComplete="off" />
      </label>
    </div>
  );
}

export function Field({ label, name, type = 'text', required, textarea, ...rest }) {
  const Tag = textarea ? 'textarea' : 'input';
  return (
    <label className="field">
      <span>{label}{required && <em style={{ color: 'var(--red)', fontStyle: 'normal' }}> *</em>}</span>
      <Tag name={name} type={textarea ? undefined : type} required={required} {...rest} />
    </label>
  );
}
